import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Users, Clock, Briefcase, AlertTriangle, ChevronRight, 
  ArrowLeft, Calendar, TrendingUp, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyTeamDashboard() {
  const { user } = useAuth();
  const [view, setView] = useState("overview"); // overview | member | member-project
  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Drill-down state
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberProjects, setMemberProjects] = useState([]);
  const [memberTimesheets, setMemberTimesheets] = useState([]);
  const [memberSummary, setMemberSummary] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectTimesheets, setProjectTimesheets] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  useEffect(() => { fetchTeamData(); }, [startDate, endDate]);

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      const res = await axios.get(`${API}/team/members?${params.toString()}`);
      setMembers(res.data?.members || []);
      setSummary(res.data?.summary || {});
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("You are not assigned as a team leader");
      } else {
        toast.error("Failed to load team data");
      }
    } finally {
      setLoading(false);
    }
  };

  const drillIntoMember = async (member) => {
    setDrillLoading(true);
    setSelectedMember(member);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      const [projRes, sumRes] = await Promise.all([
        axios.get(`${API}/team/member/${member.id}/projects?${params.toString()}`),
        axios.get(`${API}/team/member/${member.id}/summary?${params.toString()}`),
      ]);
      setMemberProjects(projRes.data?.projects || []);
      setMemberSummary(sumRes.data?.stats || {});
      setView("member");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load member data");
    } finally {
      setDrillLoading(false);
    }
  };

  const drillIntoProject = async (project) => {
    setDrillLoading(true);
    setSelectedProject(project);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      params.set("project_id", project.project_id);
      const res = await axios.get(`${API}/team/member/${selectedMember.id}/timesheets?${params.toString()}`);
      setProjectTimesheets(res.data?.timesheets || []);
      setView("member-project");
    } catch (err) {
      toast.error("Failed to load project timesheets");
    } finally {
      setDrillLoading(false);
    }
  };

  const goBack = () => {
    if (view === "member-project") {
      setView("member");
      setSelectedProject(null);
      setProjectTimesheets([]);
    } else if (view === "member") {
      setView("overview");
      setSelectedMember(null);
      setMemberProjects([]);
      setMemberSummary(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="my-team-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="my-team-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          {view !== "overview" && (
            <Button variant="ghost" size="sm" onClick={goBack} data-testid="team-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" />Back
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {view === "overview" && "My Team"}
              {view === "member" && selectedMember?.name}
              {view === "member-project" && `${selectedProject?.project_name}`}
            </h1>
            <p className="text-sm text-muted-foreground">
              {view === "overview" && "Team performance overview and drill-down"}
              {view === "member" && `Projects and performance for ${selectedMember?.name}`}
              {view === "member-project" && `${selectedMember?.name} → ${selectedProject?.project_name}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">From</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-36" data-testid="team-start-date" />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs whitespace-nowrap">To</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-36" data-testid="team-end-date" />
          </div>
          <Button variant="outline" size="sm" onClick={fetchTeamData} data-testid="team-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* OVERVIEW VIEW */}
      {view === "overview" && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary/10"><Users className="w-5 h-5 text-primary" /></div>
                  <div>
                    <p className="text-2xl font-bold">{summary?.total_members || 0}</p>
                    <p className="text-xs text-muted-foreground">Team Members</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-500/10"><Clock className="w-5 h-5 text-emerald-600" /></div>
                  <div>
                    <p className="text-2xl font-bold">{summary?.total_hours || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10"><Briefcase className="w-5 h-5 text-blue-600" /></div>
                  <div>
                    <p className="text-2xl font-bold">{summary?.total_projects || 0}</p>
                    <p className="text-xs text-muted-foreground">Active Projects</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
                  <div>
                    <p className="text-2xl font-bold">{summary?.flags?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Flags / Issues</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Flags */}
          {summary?.flags?.length > 0 && (
            <Card className="border-amber-200 dark:border-amber-800">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium">Attention Required</span>
                </div>
                <ul className="space-y-1">
                  {summary.flags.map((f, i) => (
                    <li key={i} className="text-sm text-muted-foreground">• <span className="font-medium">{f.member}</span>: {f.issue}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Team Members Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Team Members</CardTitle>
              <CardDescription>Click a member to drill into their data</CardDescription>
            </CardHeader>
            <CardContent>
              {members.length === 0 ? (
                <p className="text-muted-foreground text-sm">No team members assigned.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium">Name</th>
                        <th className="pb-3 font-medium">Role</th>
                        <th className="pb-3 font-medium text-right">Total Hours</th>
                        <th className="pb-3 font-medium text-right">Fee Hours</th>
                        <th className="pb-3 font-medium text-right">Projects</th>
                        <th className="pb-3 font-medium">Utilization</th>
                        <th className="pb-3 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => (
                        <tr 
                          key={m.id} 
                          className="border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-colors"
                          onClick={() => drillIntoMember(m)}
                          data-testid={`team-member-row-${m.id}`}
                        >
                          <td className="py-3">
                            <div className="font-medium">{m.name}</div>
                            <div className="text-xs text-muted-foreground">{m.email}</div>
                          </td>
                          <td className="py-3"><Badge variant="outline" className="text-xs">{m.role}</Badge></td>
                          <td className="py-3 text-right font-mono">{m.total_hours}</td>
                          <td className="py-3 text-right font-mono">{m.fee_hours}</td>
                          <td className="py-3 text-right">{m.active_projects}</td>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <Progress value={m.utilization} className="h-2 w-16" />
                              <span className="text-xs font-medium">{m.utilization}%</span>
                            </div>
                          </td>
                          <td className="py-3"><ChevronRight className="w-4 h-4 text-muted-foreground" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* MEMBER DRILL-DOWN VIEW */}
      {view === "member" && selectedMember && (
        <>
          {drillLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
          ) : (
            <>
              {/* Member Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-xl font-bold">{memberSummary?.total_hours || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Hours</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-xl font-bold">{memberSummary?.fee_hours || 0}</p>
                    <p className="text-xs text-muted-foreground">Fee Hours</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-xl font-bold">{memberSummary?.non_fee_hours || 0}</p>
                    <p className="text-xs text-muted-foreground">Non-Fee Hours</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-xl font-bold">{memberSummary?.utilization || 0}%</p>
                    <p className="text-xs text-muted-foreground">Utilization</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-xl font-bold">{memberSummary?.active_projects || 0}</p>
                    <p className="text-xs text-muted-foreground">Projects</p>
                  </CardContent>
                </Card>
              </div>

              {/* Member Projects Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Projects</CardTitle>
                  <CardDescription>All projects {selectedMember.name} is involved in. Click to view time entries.</CardDescription>
                </CardHeader>
                <CardContent>
                  {memberProjects.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No projects found for selected period.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-3 font-medium">Project</th>
                            <th className="pb-3 font-medium">Client</th>
                            <th className="pb-3 font-medium text-right">Actual Hours</th>
                            <th className="pb-3 font-medium text-right">Quoted Hours</th>
                            <th className="pb-3 font-medium text-right">Variance</th>
                            <th className="pb-3 font-medium text-right">Value (GBP)</th>
                            <th className="pb-3 font-medium text-right">Entries</th>
                            <th className="pb-3"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {memberProjects.map((p) => (
                            <tr 
                              key={p.project_id} 
                              className="border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-colors"
                              onClick={() => drillIntoProject(p)}
                              data-testid={`member-project-row-${p.project_id}`}
                            >
                              <td className="py-3">
                                <div className="font-medium">{p.project_name}</div>
                                <div className="text-xs text-muted-foreground">{p.project_id}</div>
                              </td>
                              <td className="py-3 text-sm">{p.client_name || "-"}</td>
                              <td className="py-3 text-right font-mono">{p.hours}</td>
                              <td className="py-3 text-right font-mono">{p.quoted_hours || "-"}</td>
                              <td className="py-3 text-right font-mono">
                                {p.variance !== 0 ? (
                                  <span className={p.variance > 0 ? "text-emerald-600" : "text-destructive"}>
                                    {p.variance > 0 ? "+" : ""}{p.variance}
                                  </span>
                                ) : "-"}
                              </td>
                              <td className="py-3 text-right font-mono">{p.project_value_gbp ? `£${p.project_value_gbp.toLocaleString()}` : "-"}</td>
                              <td className="py-3 text-right">{p.entries}</td>
                              <td className="py-3"><ChevronRight className="w-4 h-4 text-muted-foreground" /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}

      {/* PROJECT DRILL-DOWN VIEW */}
      {view === "member-project" && selectedProject && (
        <>
          {drillLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
          ) : (
            <>
              {/* Project Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-xl font-bold">{selectedProject.hours}</p>
                    <p className="text-xs text-muted-foreground">Actual Hours</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-xl font-bold">{selectedProject.quoted_hours || "-"}</p>
                    <p className="text-xs text-muted-foreground">Quoted Hours</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className={`text-xl font-bold ${selectedProject.variance > 0 ? "text-emerald-600" : selectedProject.variance < 0 ? "text-destructive" : ""}`}>
                      {selectedProject.variance > 0 ? "+" : ""}{selectedProject.variance || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Variance (hrs)</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-xl font-bold">{projectTimesheets.length}</p>
                    <p className="text-xs text-muted-foreground">Time Entries</p>
                  </CardContent>
                </Card>
              </div>

              {/* Timesheets */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Time Entries</CardTitle>
                  <CardDescription>{selectedMember?.name} on {selectedProject.project_name}</CardDescription>
                </CardHeader>
                <CardContent>
                  {projectTimesheets.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No time entries found.</p>
                  ) : (
                    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-card">
                          <tr className="border-b text-left">
                            <th className="pb-2 font-medium">Date</th>
                            <th className="pb-2 font-medium">Hours</th>
                            <th className="pb-2 font-medium">Type</th>
                            <th className="pb-2 font-medium">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectTimesheets.map((t, i) => (
                            <tr key={i} className="border-b border-border/50">
                              <td className="py-2 text-xs font-mono">{t.date}</td>
                              <td className="py-2 font-mono font-medium">{t.hours}</td>
                              <td className="py-2">
                                <Badge 
                                  variant={t.fee_earning?.toLowerCase().includes("non") ? "secondary" : "default"} 
                                  className="text-xs"
                                >
                                  {t.fee_earning || "Unknown"}
                                </Badge>
                              </td>
                              <td className="py-2 text-xs text-muted-foreground max-w-xs truncate">{t.description || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
